import { useEffect, useMemo, useState } from "react";
import {
  businessGoalOptions,
  pricePreferenceOptions,
  trafficSourceOptions,
} from "@/features/tools/monetisationModelOptions";
import type { Project } from "@/features/projects/projectTypes";
import type {
  Blueprint,
  MonetisationBusinessGoal,
  MonetisationModelRequest,
  MonetisationPricePreference,
  MonetisationTrafficSource,
} from "@/features/tools/proToolTypes";

type GeneratedBlueprintOption = Pick<
  Blueprint,
  "id" | "project_id" | "title" | "target_builder" | "created_at" | "updated_at"
>;

type MonetisationModelFormProps = {
  projects: Project[];
  generatedBlueprints: GeneratedBlueprintOption[];
  isSubmitting: boolean;
  error: string | null;
  onSubmit: (input: MonetisationModelRequest) => void;
};

type InputMode = "blueprint" | "manual";

const MANUAL_DESCRIPTION_MIN_LENGTH = 40;
const MANUAL_DESCRIPTION_HELPER =
  "Describe what the app does, who it helps, and the problem it solves.";
const MANUAL_DESCRIPTION_ERROR =
  "Please add a bit more detail so the suggester can create a useful monetisation recommendation.";
const MANUAL_DESCRIPTION_EXAMPLE =
  "Example: A simple app that helps coaches track client goals, weekly actions, progress notes, and next steps.";

const vagueManualDescriptions = new Set([
  "app",
  "an app",
  "just app",
  "simple app",
  "my app",
  "test",
  "test app",
  "app idea",
  "build app",
  "something",
]);

function getInitialMode(blueprints: GeneratedBlueprintOption[]): InputMode {
  return blueprints.length > 0 ? "blueprint" : "manual";
}

function getInitialProjectId(
  projects: Project[],
  blueprints: GeneratedBlueprintOption[],
) {
  return blueprints[0]?.project_id ?? projects[0]?.id ?? "";
}

function formatBuilder(value: string) {
  return value
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function isManualDescriptionMeaningful(description: string) {
  const normalized = description.trim().toLowerCase().replace(/\s+/g, " ");

  return (
    normalized.length >= MANUAL_DESCRIPTION_MIN_LENGTH &&
    !vagueManualDescriptions.has(normalized)
  );
}

export function MonetisationModelForm({
  projects,
  generatedBlueprints,
  isSubmitting,
  error,
  onSubmit,
}: MonetisationModelFormProps) {
  const [inputMode, setInputMode] = useState<InputMode>(() =>
    getInitialMode(generatedBlueprints),
  );
  const [hasChosenMode, setHasChosenMode] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState(() =>
    getInitialProjectId(projects, generatedBlueprints),
  );
  const [selectedBlueprintId, setSelectedBlueprintId] = useState("");
  const [appDescription, setAppDescription] = useState("");
  const [targetAudience, setTargetAudience] = useState("");
  const [businessGoal, setBusinessGoal] = useState<MonetisationBusinessGoal | "">(
    "",
  );
  const [trafficSource, setTrafficSource] =
    useState<MonetisationTrafficSource>("unknown");
  const [pricePreference, setPricePreference] =
    useState<MonetisationPricePreference>("not_sure");
  const [extraContext, setExtraContext] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);

  const filteredBlueprints = useMemo(
    () =>
      generatedBlueprints.filter(
        (blueprint) => blueprint.project_id === selectedProjectId,
      ),
    [generatedBlueprints, selectedProjectId],
  );

  useEffect(() => {
    if (!hasChosenMode && inputMode === "manual" && generatedBlueprints.length > 0) {
      setInputMode("blueprint");
      return;
    }

    if (
      !selectedProjectId &&
      (projects.length > 0 || generatedBlueprints.length > 0)
    ) {
      setSelectedProjectId(getInitialProjectId(projects, generatedBlueprints));
    }
  }, [
    generatedBlueprints,
    hasChosenMode,
    inputMode,
    projects,
    selectedProjectId,
  ]);

  useEffect(() => {
    if (inputMode !== "blueprint") {
      return;
    }

    const selectedStillAvailable = filteredBlueprints.some(
      (blueprint) => blueprint.id === selectedBlueprintId,
    );

    if (!selectedStillAvailable) {
      setSelectedBlueprintId(filteredBlueprints[0]?.id ?? "");
    }
  }, [filteredBlueprints, inputMode, selectedBlueprintId]);

  const selectedBlueprint = useMemo(
    () =>
      filteredBlueprints.find(
        (blueprint) => blueprint.id === selectedBlueprintId,
      ),
    [filteredBlueprints, selectedBlueprintId],
  );

  const canSubmit = useMemo(() => {
    if (inputMode === "blueprint") {
      return Boolean(selectedBlueprint);
    }

    return isManualDescriptionMeaningful(appDescription);
  }, [appDescription, inputMode, selectedBlueprint]);

  const manualDescriptionState = useMemo(() => {
    if (inputMode !== "manual") {
      return null;
    }

    if (!appDescription.trim()) {
      return {
        tone: "helper" as const,
        message: MANUAL_DESCRIPTION_HELPER,
      };
    }

    if (!isManualDescriptionMeaningful(appDescription)) {
      return {
        tone: "error" as const,
        message: MANUAL_DESCRIPTION_ERROR,
      };
    }

    return {
      tone: "helper" as const,
      message: "This is enough detail to suggest a monetisation path.",
    };
  }, [appDescription, inputMode]);

  function handleSubmit() {
    setLocalError(null);

    if (!canSubmit) {
      setLocalError(
        inputMode === "manual"
          ? MANUAL_DESCRIPTION_ERROR
          : "Select a generated blueprint before generating a monetisation suggestion.",
      );
      return;
    }

    onSubmit({
      blueprintId: inputMode === "blueprint" ? selectedBlueprint?.id : undefined,
      projectId:
        inputMode === "blueprint" ? selectedBlueprint?.project_id : undefined,
      appDescription,
      targetAudience,
      businessGoal: businessGoal || undefined,
      trafficSource,
      pricePreference,
      extraContext,
    });
  }

  return (
    <section className="min-w-0 rounded-lg border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
      <div>
        <p className="text-sm font-semibold text-indigo-700">
          Monetisation Model Suggester
        </p>
        <h3 className="mt-2 text-xl font-semibold text-slate-950">
          Find the best commercial path for this app
        </h3>
        <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
          Select a generated blueprint, add any audience or offer context, and
          get a practical first monetisation recommendation.
        </p>
      </div>

      {error || localError ? (
        <div className="mt-5 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {localError ?? error}
        </div>
      ) : null}

      <div className="mt-6">
        <div className="flex w-full flex-wrap rounded-lg border border-slate-200 bg-slate-50 p-1 sm:w-auto">
          {(
            [
              ["blueprint", "Use generated blueprint"],
              ["manual", "Describe manually"],
            ] as Array<[InputMode, string]>
          ).map(([mode, label]) => (
            <button
              key={mode}
              type="button"
              onClick={() => {
                setInputMode(mode);
                setHasChosenMode(true);
                setLocalError(null);
              }}
              className={`flex-1 whitespace-normal rounded-md px-3 py-2 text-sm font-semibold transition sm:flex-none ${
                inputMode === mode
                  ? "bg-white text-indigo-700 shadow-sm"
                  : "text-slate-600 hover:text-slate-950"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6 grid min-w-0 gap-5 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)]">
        <div className="min-w-0">
          {inputMode === "blueprint" ? (
            <div className="grid min-w-0 gap-4 sm:grid-cols-2">
              <div className="min-w-0">
                <label
                  htmlFor="monetisation-project"
                  className="block text-sm font-medium text-slate-700"
                >
                  Project
                </label>
                <select
                  id="monetisation-project"
                  value={selectedProjectId}
                  onChange={(event) => {
                    setSelectedProjectId(event.target.value);
                    setSelectedBlueprintId("");
                  }}
                  className="mt-2 w-full min-w-0 max-w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-950 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
                >
                  {projects.length > 0 ? (
                    projects.map((project) => (
                      <option key={project.id} value={project.id}>
                        {project.name}
                      </option>
                    ))
                  ) : (
                    <option value="">No projects found</option>
                  )}
                </select>
              </div>

              <div className="min-w-0">
                <label
                  htmlFor="monetisation-blueprint"
                  className="block text-sm font-medium text-slate-700"
                >
                  Generated blueprint
                </label>
                <select
                  id="monetisation-blueprint"
                  value={selectedBlueprintId}
                  onChange={(event) => setSelectedBlueprintId(event.target.value)}
                  disabled={filteredBlueprints.length === 0}
                  className="mt-2 w-full min-w-0 max-w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-950 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500"
                >
                  {filteredBlueprints.length > 0 ? (
                    filteredBlueprints.map((blueprint) => (
                      <option key={blueprint.id} value={blueprint.id}>
                        {blueprint.title}
                      </option>
                    ))
                  ) : (
                    <option value="">
                      This project does not have a generated blueprint yet.
                    </option>
                  )}
                </select>
              </div>
              <div className="sm:col-span-2">
                <p className="text-xs text-slate-500">
                  Choose the project first, then select the generated blueprint
                  you want to analyse.
                </p>
                {selectedBlueprint ? (
                  <p className="mt-1 text-xs text-slate-500">
                    Target builder:{" "}
                    {formatBuilder(selectedBlueprint.target_builder)}
                  </p>
                ) : filteredBlueprints.length === 0 ? (
                  <p className="mt-1 text-xs font-medium text-amber-700">
                    This project does not have a generated blueprint yet.
                  </p>
                ) : null}
              </div>
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-4 text-sm leading-6 text-slate-600">
              Manual input is available when you want a recommendation for an
              app that does not have a generated blueprint yet. Add enough
              detail below so the suggestion is specific.
            </div>
          )}
        </div>

        <div className="rounded-lg border border-indigo-100 bg-indigo-50 p-4 text-sm leading-6 text-indigo-950">
          <p className="font-semibold">Blueprint-first by default</p>
          <p className="mt-1">
            The strongest recommendations come from your generated blueprint.
            Audience and traffic details help make the advice more commercial.
          </p>
        </div>
      </div>

      {inputMode === "manual" ? (
        <div className="mt-6">
          <label
            htmlFor="monetisation-description"
            className="block text-sm font-medium text-slate-700"
          >
            Manual app description
          </label>
          <textarea
            id="monetisation-description"
            value={appDescription}
            onChange={(event) => {
              setAppDescription(event.target.value);
              setLocalError(null);
            }}
            maxLength={4000}
            rows={5}
            className="mt-2 w-full min-w-0 max-w-full resize-y rounded-md border border-slate-300 bg-white px-3 py-2 text-sm leading-6 text-slate-950 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
            placeholder="Describe the app, target user, core workflow, and the value it creates in at least 40 characters."
          />
          <p className="mt-2 text-xs text-slate-500">
            {appDescription.length} / 4000
          </p>
          {manualDescriptionState ? (
            <div
              className={`mt-2 rounded-md border p-3 text-sm leading-6 ${
                manualDescriptionState.tone === "error"
                  ? "border-amber-200 bg-amber-50 text-amber-900"
                  : "border-slate-200 bg-slate-50 text-slate-600"
              }`}
            >
              <p>{manualDescriptionState.message}</p>
              <p className="mt-1">{MANUAL_DESCRIPTION_EXAMPLE}</p>
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="mt-6 grid min-w-0 gap-4 lg:grid-cols-2">
        <div>
          <label
            htmlFor="monetisation-audience"
            className="block text-sm font-medium text-slate-700"
          >
            Target audience
          </label>
          <input
            id="monetisation-audience"
            value={targetAudience}
            onChange={(event) => setTargetAudience(event.target.value)}
            maxLength={500}
            className="mt-2 w-full min-w-0 max-w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-950 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
            placeholder="Example: coaches, course creators, small agencies"
          />
          <p className="mt-2 text-xs text-slate-500">
            Recommended, but optional when the blueprint is clear.
          </p>
        </div>

        <div>
          <label
            htmlFor="monetisation-business-goal"
            className="block text-sm font-medium text-slate-700"
          >
            Business goal
          </label>
          <select
            id="monetisation-business-goal"
            value={businessGoal}
            onChange={(event) =>
              setBusinessGoal(event.target.value as MonetisationBusinessGoal | "")
            }
            className="mt-2 w-full min-w-0 max-w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-950 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
          >
            <option value="">Not sure yet</option>
            {businessGoalOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label
            htmlFor="monetisation-traffic-source"
            className="block text-sm font-medium text-slate-700"
          >
            Traffic source
          </label>
          <select
            id="monetisation-traffic-source"
            value={trafficSource}
            onChange={(event) =>
              setTrafficSource(event.target.value as MonetisationTrafficSource)
            }
            className="mt-2 w-full min-w-0 max-w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-950 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
          >
            {trafficSourceOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label
            htmlFor="monetisation-price-preference"
            className="block text-sm font-medium text-slate-700"
          >
            Price preference
          </label>
          <select
            id="monetisation-price-preference"
            value={pricePreference}
            onChange={(event) =>
              setPricePreference(
                event.target.value as MonetisationPricePreference,
              )
            }
            className="mt-2 w-full min-w-0 max-w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-950 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
          >
            {pricePreferenceOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="mt-6">
        <label
          htmlFor="monetisation-extra-context"
          className="block text-sm font-medium text-slate-700"
        >
          Optional extra context
        </label>
        <textarea
          id="monetisation-extra-context"
          value={extraContext}
          onChange={(event) => setExtraContext(event.target.value)}
          maxLength={1000}
          rows={4}
          className="mt-2 w-full min-w-0 max-w-full resize-y rounded-md border border-slate-300 bg-white px-3 py-2 text-sm leading-6 text-slate-950 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
          placeholder="Add current audience size, existing offer, preferred price point, or anything commercial that matters."
        />
        <p className="mt-2 text-xs text-slate-500">
          {extraContext.length} / 1000
        </p>
      </div>

      <div className="mt-6 flex justify-stretch sm:justify-end">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!canSubmit || isSubmitting}
          className="w-full rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-indigo-300 sm:w-auto"
        >
          {isSubmitting
            ? "Analysing your monetisation options..."
            : "Suggest monetisation model"}
        </button>
      </div>
    </section>
  );
}
